/* =========================================================================
 * Kkumi · Wishlist Grid — live renderer
 * -------------------------------------------------------------------------
 * Pipeline (confirmed via probes):
 *   1) GET {WK.config.routes.apiHost}/wishlists/mine  (WK wishlist headers)
 *        -> { wishlist: { items: [{ id, productId, variantId }] } }
 *   2) Batch-resolve productIds via Shopify Storefront GraphQL nodes()
 *        token   = WK.config.storefront.accessToken
 *        version = WK.config.storefront.version (fallback 2024-10)
 *   3) Render grid/list cards into the section, replacing placeholders.
 *
 * Wired actions:
 *   - Unsave (heart)  -> WK.removeWishlistItem(...), or WK.removeWithUndo(...)
 *                        under the account tab's Undo toast (KkwlBus 'removed')
 *   - Add to cart     -> /cart/add.js + cart-drawer.renderContents()
 *   - View            -> product link
 * (Add-all / share / copy live in the toolbar section — wired separately.)
 * ========================================================================= */
(function () {
  'use strict';

  var roots = document.querySelectorAll('[data-kkwl-grid]');
  if (!roots.length) return;

  // v11: match the loading ghosts to the member's last known item count (the
  // wishlist client's localStorage cache), capped at one page. No cache →
  // the one row the section renders.
  (function sizeGhosts() {
    var cfg = window.__kkWishCfg || {};
    var n = 0, boughtN = 0;
    try {
      var c = JSON.parse(localStorage.getItem('kkwl:cache:' + (cfg.customerId || 'anon') + ':v2'));
      var cached = c && c.wl && c.wl.items ? c.wl.items : [];
      n = cached.length;
      cached.forEach(function (it) { if (it && Number(it.purchasedAt) > 0) boughtN++; });
    } catch (e) { n = 0; }
    if (!n) return;
    roots.forEach(function (root) {
      if (root.getAttribute('data-kkwl-v11') !== 'true') return;
      var sk = root.querySelector('.kkwl-grid__skeleton');
      var ghosts = sk ? sk.children : [];
      if (!ghosts.length) return;
      var want = Math.min(n, 12);
      while (ghosts.length > want) sk.removeChild(sk.lastElementChild);
      while (ghosts.length < want) sk.appendChild(ghosts[0].cloneNode(true));
      // Bought cards sit last (positions n - boughtN … n - 1) and carry one more row (the tag and
      // Remove). The ghosts standing in for them get a bar for it, so the real cards landing don't
      // push the page down. A long list's first page has none of them.
      if (boughtN && window.KKSkel && root.getAttribute('data-kkwl-bought') === 'true') {
        for (var gi = Math.max(0, n - boughtN); gi < want; gi++) {
          var body = ghosts[gi].querySelector('.kk-pc__body');
          var price = body && body.querySelector('.kkwl-ghost--price');
          if (body && price) body.insertBefore(window.KKSkel.el({ 'class': 'kkwl-ghost--bought' }), price);
        }
      }
    });
  })();

  /* ---------------- utilities ---------------- */
  var esc = window.KKEsc; // canonical shared escaper (assets/kkumi-esc.js)
  var MONEY_FORMAT = (roots[0] && roots[0].getAttribute('data-kkwl-money-format')) || '${{amount}}';

  // Merchant-editable wishlist copy (data-* on the section root). Each falls back
  // to its original literal so the grid renders identically with no settings
  // wired. The card's own copy (tags, prices, buttons, rating) is the sitewide
  // Theme settings → Kkumi product cards, read by window.KKCard.
  function _attr(name, dflt) { return (roots[0] && roots[0].getAttribute(name)) || dflt; }
  var LABELS = {
    savedToday:  _attr('data-kkwl-saved-today', 'Saved today'),
    savedDay:    _attr('data-kkwl-saved-day', 'Saved [n] day ago'),
    savedDays:   _attr('data-kkwl-saved-days', 'Saved [n] days ago'),
    savedWeek:   _attr('data-kkwl-saved-week', 'Saved [n] week ago'),
    savedWeeks:  _attr('data-kkwl-saved-weeks', 'Saved [n] weeks ago'),
    savedMonth:  _attr('data-kkwl-saved-month', 'Saved [n] month ago'),
    savedMonths: _attr('data-kkwl-saved-months', 'Saved [n] months ago'),
    savedYear:   _attr('data-kkwl-saved-year', 'Saved [n] year ago'),
    savedYears:  _attr('data-kkwl-saved-years', 'Saved [n] years ago'),
    removeAria:  _attr('data-kkwl-remove-aria', 'Remove [title] from wishlist'),
    selectItem:  _attr('data-kkwl-select-item', 'Select item'),
    adding:      _attr('data-kkwl-adding', 'Adding…'),
    productFallback: _attr('data-kkwl-product-fallback', 'Product')
  };
  function fill(tpl, token, value) { return tpl.split(token).join(value); }
  function formatWithDelimiters(cents, precision, thousands, decimal) {
    precision = precision == null ? 2 : precision;
    thousands = thousands || ',';
    decimal = decimal || '.';
    if (isNaN(cents) || cents == null) return '0';
    cents = (cents / 100).toFixed(precision);
    var parts = cents.split('.');
    var dollars = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1' + thousands);
    return dollars + (parts[1] ? decimal + parts[1] : '');
  }
  function moneyFromDollars(amount) {
    // `amount` is a dollar value (Shopify Money .amount string). The sibling
    // recommendations module's moneyFromCents() takes CENTS — the unit is in
    // the name on purpose so the two can never be confused or merged blindly.
    if (amount == null || amount === '') return '';
    var cents = Math.round(parseFloat(amount) * 100);
    if (isNaN(cents)) return '';
    var ph = MONEY_FORMAT.match(/\{\{\s*(\w+)\s*\}\}/);
    var token = ph ? ph[1] : 'amount';
    var val;
    switch (token) {
      case 'amount_no_decimals': val = formatWithDelimiters(cents, 0); break;
      case 'amount_with_comma_separator': val = formatWithDelimiters(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': val = formatWithDelimiters(cents, 0, '.', ','); break;
      case 'amount_with_space_separator': val = formatWithDelimiters(cents, 2, ' ', ','); break;
      case 'amount_with_apostrophe_separator': val = formatWithDelimiters(cents, 2, "'", '.'); break;
      default: val = formatWithDelimiters(cents, 2);
    }
    return ph ? MONEY_FORMAT.replace(ph[0], val) : MONEY_FORMAT + val;
  }
  function imgUrl(url, w) {
    if (!url) return '';
    return url + (url.indexOf('?') >= 0 ? '&' : '?') + 'width=' + w;
  }
  function numId(gid) { var m = String(gid).match(/(\d+)\s*$/); return m ? m[1] : String(gid); }
  // Items carry a real `createdAt` (ms epoch) from the custom backend.
  function savedAgo(ts) {
    ts = Number(ts);
    if (!ts || isNaN(ts)) return '';
    var diff = Date.now() - ts, day = 86400000;
    if (diff < 0) return '';
    if (diff < day) return LABELS.savedToday;
    var d = Math.floor(diff / day);
    if (d < 7) return fill(d === 1 ? LABELS.savedDay : LABELS.savedDays, '[n]', d);
    if (d < 30) { var w = Math.floor(d / 7); return fill(w === 1 ? LABELS.savedWeek : LABELS.savedWeeks, '[n]', w); }
    if (d < 365) { var mo = Math.floor(d / 30); return fill(mo === 1 ? LABELS.savedMonth : LABELS.savedMonths, '[n]', mo); }
    var y = Math.floor(d / 365); return fill(y === 1 ? LABELS.savedYear : LABELS.savedYears, '[n]', y);
  }

  /* ---------------- bought items ---------------- *
   * The orders webhook marks an item bought and the member's own list hands it over as
   * `purchasedAt` (ms). A shared list never carries it, and the read-only view ignores it
   * anyway. A bought card wears a "Bought 12 Oct" tag and a Remove button and sits after every
   * item not bought — in the saved order, in search results and across pages. Copy, colours and
   * the on/off switch are the grid section's "Bought items" settings (data-kkwl-bought-*). */
  // "12 Oct" in the shopper's own date style (their browser's locale and time zone).
  function boughtDate(ts) {
    try { return new Date(Number(ts)).toLocaleDateString(undefined, { day: 'numeric', month: 'short' }); } catch (e) { return ''; }
  }
  function boughtLabel(copy, ts) {
    return fill(copy.label, '[date]', boughtDate(ts)).replace(/\s+/g, ' ').trim();
  }
  function icon(name) { return window.KKIcons ? window.KKIcons.html(name) : ''; }
  // The tag + Remove row (twin of the demo card in sections/kkumi-wishlist-grid.liquid).
  function boughtRowHTML(title, ts, copy) {
    return '<div class="kkwl-bought" data-kkwl-bought-row>' +
      '<span class="kkwl-bought__tag">' + icon('check') + '<span>' + esc(boughtLabel(copy, ts)) + '</span></span>' +
      '<button type="button" class="button button--ghost button--small kkwl-bought__remove" data-action="remove-bought" aria-label="' + esc(fill(LABELS.removeAria, '[title]', title)) + '">' + icon('x') + '<span>' + esc(copy.remove) + '</span></button>' +
    '</div>';
  }
  // Items not bought first, bought last; each group keeps the order it came in.
  function boughtLast(items, opts) {
    if (!opts.bought || opts.readOnly) return items;
    var keep = [], bought = [];
    items.forEach(function (it) { (Number(it.purchasedAt) > 0 ? bought : keep).push(it); });
    return keep.concat(bought);
  }

  /* ---------------- WishlistKing wait ---------------- */
  var waitForWK = window.KKWaitForWK; // shared poller (assets/kkumi-wishlist-king.js)

  /* ---------------- data layer ---------------- */
  var shareMeta = null;

  function fetchWishlist(WK) {
    var apiHost = WK.config.routes.apiHost;
    var headers = WK._apis.wishlist.headers;
    return fetch(apiHost + '/wishlists/mine', { headers: headers })
      .then(function (r) { return r.json(); })
      .then(function (d) {
        var wl = (d && d.wishlist) || {};
        var items = wl.items || [];
        shareMeta = { publicId: wl.publicId, id: wl.id };
        return items;
      });
  }

  function resolveProducts(WK, ids) {
    var cfg = (WK.config && WK.config.storefront) || {};
    var token = cfg.accessToken;
    var ver = cfg.version || '2024-10';
    // No token yet = we can't tell which products exist. Answer "unknown"
    // (null), never an empty map: pruneZombies treats a map as authoritative
    // and would delete every saved item.
    if (!token) { return Promise.resolve(null); }
    var gids = ids.map(function (id) { return 'gid://shopify/Product/' + id; });
    var query =
      '{ nodes(ids: ' + JSON.stringify(gids) + ') { ... on Product { ' +
      'id title handle onlineStoreUrl availableForSale productType vendor tags ' +
      'featuredImage { url altText } ' +
      // Pull each variant's image so a variant-level wishlist save can render
      // its own image instead of the product hero (the saved variant is the
      // exact thing the customer wants to see).
      'variants(first: 100) { nodes { id title availableForSale currentlyNotInStock image { url altText } ' +
      'price { amount } compareAtPrice { amount } selectedOptions { name value } } } ' +
      // Product-card inputs (KKCard.state): first option for the badge / sold-out
      // note, and the card metafields. A metafield the storefront can't read
      // just comes back null — the card falls back as the Liquid one does.
      'options(first: 3) { name values } ' +
      'cardTitleMeta: metafield(namespace: "custom", key: "card_title") { value } ' +
      'preorderMeta: metafield(namespace: "kkumi", key: "preorder") { value } ' +
      'preorderDateMeta: metafield(namespace: "kkumi", key: "preorder_ship_date") { value } ' +
      'preorderNoteMeta: metafield(namespace: "kkumi", key: "preorder_note") { value } ' +
      'bundleItemsMeta: metafield(namespace: "kkumi", key: "bundle_items") { value ' +
      'references(first: 50) { nodes { ... on Product { id } ... on ProductVariant { product { id } } } } } ' +
      'ratingMeta: metafield(namespace: "reviews", key: "rating") { value } ' +
      'ratingCountMeta: metafield(namespace: "reviews", key: "rating_count") { value } ' +
      'priceRange { minVariantPrice { amount currencyCode } maxVariantPrice { amount } } ' +
      'compareAtPriceRange { minVariantPrice { amount } } } } }';
    return fetch('/api/' + ver + '/graphql.json', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Shopify-Storefront-Access-Token': token },
      body: JSON.stringify({ query: query })
    })
      .then(function (r) { return r.json(); })
      .then(function (res) {
        // null = the request itself failed (treated as "unknown", never pruned);
        // a map (even empty) = authoritative answer about which products exist.
        if (!res || !res.data) { return null; }
        var map = {};
        var nodes = (res.data && res.data.nodes) || [];
        nodes.forEach(function (n) { if (n && n.id) map[numId(n.id)] = n; });
        return map;
      })
      .catch(function (e) { return null; });
  }

  // Items whose product no longer resolves on the storefront (deleted /
  // unpublished) can't be viewed, carted or restock-notified — remove them so
  // the count, the grid and the empty state all agree. Only runs on an
  // authoritative resolution (map !== null), never on a failed request.
  function pruneZombies(WK, items, map) {
    if (designMode || !map) return false;
    var pruned = false;
    items.forEach(function (it) {
      if (map[it.productId]) return;
      pruned = true;
      try {
        WK.removeWishlistItem({
          wishlistItemId: it.id,
          productId: Number(it.productId),
          // Pass raw (null / string), not Number(...) || 0 — kkumi-wishlist-core
          // keys cache items as `productId|variantId` where a missing variant
          // is `''`, but `Number(null) || 0` would key it as `'0'` and miss,
          // leaving the local count stuck at its pre-prune value until the
          // 5-min cache TTL.
          variantId: it.variantId == null ? null : it.variantId,
          inWishlist: true
        });
      } catch (e) {}
    });
    return pruned;
  }

  // "Back in stock" tag window: the product came back within this long.
  var BACK_MS = 14 * 86400000;

  /* ---------------- card model ---------------- *
   * The card itself is the sitewide product card: KKCard.state applies the
   * shared rules (tag, badge, note, price row) and KKCard.html writes the
   * same markup as snippets/kkumi-product-card-shell.liquid — this file only
   * maps the Storefront API product onto it and adds the wishlist extras
   * (select check, remove heart, "Saved …" line, earn-points line). */
  // Reviews from the Shopify standard "reviews" namespace (populated by Judge.me).
  // reviews.rating value is JSON like {"value":"4.9","scale_max":"5.0"}.
  function parseRating(product) {
    var count = 0, value = 0;
    if (product.ratingCountMeta && product.ratingCountMeta.value) count = parseInt(product.ratingCountMeta.value, 10) || 0;
    if (count > 0 && product.ratingMeta && product.ratingMeta.value) {
      try { value = parseFloat(JSON.parse(product.ratingMeta.value).value) || 0; }
      catch (e) { value = parseFloat(product.ratingMeta.value) || 0; }
    }
    return { value: value, count: count };
  }
  function cents(money) { return money && money.amount != null ? Math.round(parseFloat(money.amount) * 100) || 0 : 0; }
  function moneyFromCents(c) { return moneyFromDollars(c / 100); }
  function metaVal(m) { return m && m.value != null ? String(m.value) : ''; }
  // kkumi.preorder_ship_date ("2026-11-14") → "14 Nov", same as the Liquid '%-d %b'.
  function shipDate(v) {
    if (!/^\d{4}-\d{2}-\d{2}/.test(v)) return '';
    var d = new Date(v.slice(0, 10) + 'T00:00:00Z');
    if (isNaN(d)) return '';
    try { return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', timeZone: 'UTC' }); } catch (e) { return ''; }
  }
  function listCount(v) { try { var a = JSON.parse(v); return Array.isArray(a) ? a.length : 0; } catch (e) { return 0; } }
  // Bundle "[count] items" — twin of the Liquid card: a multi-variant bundle
  // lists every component variant across its versions (4 x 3 = 12 entries),
  // so there each component PRODUCT counts once; a single-version bundle
  // counts every entry.
  function bundleCount(meta, multiVariant) {
    var n = listCount(metaVal(meta));
    var nodes = meta && meta.references && meta.references.nodes;
    if (!multiVariant || !nodes || !nodes.length) return n;
    var seen = {};
    var distinct = 0;
    nodes.forEach(function (r) {
      var id = (r && r.product && r.product.id) || (r && r.id);
      if (id && !seen[id]) { seen[id] = true; distinct++; }
    });
    return distinct || n;
  }

  function cardModel(item, product, idx) {
    var price = product.priceRange && product.priceRange.minVariantPrice;
    var priceAmt = price ? parseFloat(price.amount) : 0;
    var cmp = product.compareAtPriceRange && product.compareAtPriceRange.minVariantPrice;
    var onSale = (cmp ? parseFloat(cmp.amount) : 0) > priceAmt && priceAmt > 0;
    var maxP = product.priceRange && product.priceRange.maxVariantPrice;
    var variantNodes = (product.variants && product.variants.nodes) || [];
    var multiVariant = variantNodes.length > 1;
    var rating = parseRating(product);
    // Deep-link to the exact saved variant on multi-variant products so a click
    // lands on what the customer saved (variant-level saves). Add-to-cart already
    // uses the saved variant id from the card root.
    var baseUrl = product.onlineStoreUrl || (product.handle ? '/products/' + product.handle : '#');
    var url = (item.variantId && multiVariant && baseUrl !== '#')
      ? baseUrl + (baseUrl.indexOf('?') >= 0 ? '&' : '?') + 'variant=' + encodeURIComponent(item.variantId)
      : baseUrl;
    // The saved variant's name, so two cards of the same product are
    // distinguishable (multiple variants can be wishlisted independently).
    // Also grab the variant's own image so the card shows the exact variant.
    var variantTitle = '';
    var variantImg = null;
    var savedNode = null;
    if (item.variantId && variantNodes.length) {
      for (var vi = 0; vi < variantNodes.length; vi++) {
        var vnid = String(variantNodes[vi].id).match(/(\d+)\s*$/);
        if (vnid && vnid[1] === String(item.variantId)) {
          savedNode = variantNodes[vi];
          // Each choice with its option name ("Colour: Blue Hour · Pack size: 50"),
          // never the bare variant title ("Pink / Pink" is ambiguous).
          if (multiVariant) variantTitle = window.KKCard.variantLabel(variantNodes[vi].selectedOptions, product.title) || variantNodes[vi].title || '';
          if (variantNodes[vi].image) variantImg = variantNodes[vi].image;
          break;
        }
      }
      if (variantTitle === 'Default Title') variantTitle = '';
    }
    var heroImg = variantImg || product.featuredImage || null;
    var title = product.title || LABELS.productFallback;
    var tags = Array.isArray(product.tags) ? product.tags : String(product.tags || '').split(',');
    var isPreorder = metaVal(product.preorderMeta) === 'true';
    var minCents = priceAmt > 0 ? Math.round(priceAmt * 100) : 0;
    // Input for the shared card rules (KKCard.state). Variants without their
    // own price (an older cached query) fall back to the product's minimum.
    var cardInput = {
      title: title,
      cardTitle: metaVal(product.cardTitleMeta),
      type: product.productType || '',
      available: product.availableForSale !== false,
      isPreorder: isPreorder,
      preorderDate: shipDate(metaVal(product.preorderDateMeta)),
      preorderNote: metaVal(product.preorderNoteMeta),
      bundleItems: bundleCount(product.bundleItemsMeta, multiVariant),
      tags: tags,
      priceVaries: !!maxP && parseFloat(maxP.amount) > priceAmt,
      options: (product.options || []).map(function (o) { return { name: o.name, values: o.values || [] }; }),
      variants: variantNodes.length ? variantNodes.map(function (vn) {
        var vals = (vn.selectedOptions || []).map(function (so) { return so.value; });
        return { available: vn.availableForSale !== false, price: vn.price ? cents(vn.price) : minCents, compareAt: cents(vn.compareAtPrice), options: vals };
      }) : [{ available: product.availableForSale !== false, price: minCents, compareAt: onSale ? cents(cmp) : 0, options: [] }]
    };
    // A variant-level save is about that exact variant: its own stock and
    // price, not the product's (a sold-out saved option used to read "Add to
    // cart" at another option's price). Other options still for sale are the
    // alternatives offered when the saved one is gone.
    var backordered = savedNode ? savedNode.currentlyNotInStock === true : variantNodes.some(function (vn) { return vn.currentlyNotInStock === true; });
    var alts = [];
    if (savedNode) {
      var savedOk = savedNode.availableForSale !== false;
      cardInput.available = savedOk;
      cardInput.priceVaries = false;
      cardInput.variants = [{ available: savedOk, price: savedNode.price ? cents(savedNode.price) : minCents, compareAt: cents(savedNode.compareAtPrice), options: (savedNode.selectedOptions || []).map(function (so) { return so.value; }) }];
      if (savedNode.price) minCents = cents(savedNode.price);
      if (!savedOk) {
        variantNodes.forEach(function (vn) {
          if (vn === savedNode || vn.availableForSale === false || vn.currentlyNotInStock !== false) return;
          var idm = String(vn.id).match(/(\d+)\s*$/);
          if (idm) alts.push({ id: idm[1], label: window.KKCard.variantLabel(vn.selectedOptions, product.title) || vn.title || '' });
        });
      }
    }
    // savedVariant = a concrete variant from a variant-level save we can add
    // straight to cart (no PDP detour). Legacy product-level saves on
    // multi-variant products still need "Choose options" because we don't
    // know which variant to add.
    // Cart line properties saved with the item — a configured bundle's choices
    // (kit, Galaxy colour, bag colour + the app's `_bundle_selection`). Adding
    // to cart replays them; the visible ones print under the title.
    var properties = (item.properties && typeof item.properties === 'object') ? item.properties : null;
    var choices = properties ? Object.keys(properties).filter(function (k) {
      return k.charAt(0) !== '_' && properties[k];
    }).map(function (k) { return properties[k]; }).join(' · ') : '';
    return {
      properties: properties,
      choices: choices,
      itemId: item.id,
      productId: item.productId,
      variantId: item.variantId,
      handle: product.handle || '',
      url: url,
      title: title,
      variantTitle: variantTitle,
      type: product.productType || '',
      vendor: product.vendor || '',
      tags: tags.join(', '),
      tagList: tags,
      img: heroImg && heroImg.url,
      imgAlt: (heroImg && heroImg.altText) || title,
      available: cardInput.available && !backordered,
      backordered: backordered,
      alts: alts,
      stock: backordered ? 'out' : cardInput.available ? (item.restockedAt && Date.now() - item.restockedAt < BACK_MS ? 'back' : 'in') : (alts.length ? 'option' : 'out'),
      alert: item.alert !== false,
      boughtAt: Number(item.purchasedAt) > 0 ? Number(item.purchasedAt) : 0, // when it was bought (ms), 0 = not bought
      isPreorder: isPreorder,
      multiVariant: multiVariant,
      savedVariant: !!item.variantId,
      onSale: onSale,
      priceCents: minCents,
      saved: savedAgo(item.createdAt),
      ratingValue: rating.value,
      ratingCount: rating.count,
      cardInput: cardInput
    };
  }

  // Wishlist-only controls (same vector data as the Liquid demo cards).
  var SVG = {
    check: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12"></polyline></svg>',
    heart: '<svg class="kkumi-card__wish-icon" width="20" height="20" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>'
  };

  function gridCard(m, opts, isHero) {
    var K = window.KKCard;
    var C = K.copy;
    var s = K.state(m.cardInput, { money: moneyFromCents });

    // The one button: sold out → the PDP's notify form; a choice to make (or a
    // pre-order, whose plan lives on the PDP) → the PDP; else straight to cart
    // with the saved variant (data-action="addcart", handled below).
    var cta;
    var v11 = opts.v11;
    // Bought: tagged + Remove, in the member's own list only. A shared (read-only) card is never bought.
    var bought = !!(opts.bought && !opts.readOnly && m.boughtAt);
    // The back-in-stock alert switch is for items still wanted: the Worker sends no alert for a
    // bought item, so its card offers the plain Notify me link instead of a switch that promises an email.
    var alertable = v11 && !opts.readOnly && !bought;
    if (m.backordered) cta = K.button({ label: C.ctaOptions, href: m.url }) + (alertable ? alertButton(m.alert, opts) : K.button({ label: C.ctaNotify, href: m.url, secondary: true }));
    else if (v11 && m.stock === 'option') cta = K.button({ label: opts.v11Copy.chooseAlt, secondary: true, attrs: { 'data-action': 'alt', 'aria-expanded': 'false' } });
    // v11 sold-out card: the item's back-in-stock alert as a switch (on by
    // default — every saved item has always been alerted).
    else if (alertable && m.stock === 'out') cta = alertButton(m.alert, opts);
    else if (!m.available) cta = K.button({ label: C.ctaNotify, href: m.url, secondary: true });
    else if (m.isPreorder) cta = K.button({ label: C.ctaPreorder, href: m.url });
    else if (m.multiVariant && !m.savedVariant) cta = K.button({ label: C.ctaOptions, href: m.url });
    else cta = K.button({ label: C.ctaAdd, attrs: { 'data-action': 'addcart' } });

    // Rewards crossover — "earn X points when you buy this". KkumiEarnPoints
    // (loaded by the section when enabled) computes the tier-adjusted number
    // and fills .kkumi-earn-points__copy; wireEarn() registers it post-render.
    var earnBlock = '';
    if (opts.showPoints && !opts.readOnly && m.priceCents > 0) {
      // Product + variant ids: a Rewards campaign tied to this item counts in the number.
      earnBlock = '<div class="kkumi-earn-points kk-pc__earn" data-scope="product" data-variant-price="' + (m.priceCents | 0) +
        '" data-product-id="' + esc(m.productId == null ? '' : String(m.productId)) + '" data-variant-id="' + esc(m.variantId == null ? '' : String(m.variantId)) +
        '" data-pattern="' + esc(opts.pointsPattern) + '" data-count-class="kk-pc__earn-count">' +
        '<span class="kkumi-earn-points__copy"></span></div>';
    }

    // Heart starts saved (these items are on the list); the grid owns removal.
    var heartHTML = opts.readOnly ? '' :
      '<button type="button" class="kk-pc__heart kkumi-card__wish wk-ready wk-active" data-action="remove" aria-pressed="true" data-tooltip="' + esc(opts.removeLabel) + '" aria-label="' + esc(fill(LABELS.removeAria, '[title]', m.title)) + '">' +
        '<span class="kk-pc__wish-spinner" aria-hidden="true"></span>' + SVG.heart +
      '</button>';
    var check = opts.noSelect ? '' : '<button type="button" class="kk-pc__check" data-action="select" aria-pressed="false" aria-label="' + esc(LABELS.selectItem) + '">' + SVG.check + '</button>';
    var edition = K.editionFromTags(m.tagList);
    // Account v11 cards: the tag is the saved variant's stock (In stock / Sold
    // out / Option sold out), no badge or note; a sold-out option carries the
    // still-for-sale alternatives, opened by "Choose another option".
    if (v11) {
      s.tag = opts.v11Copy[{ in: 'tagIn', back: 'tagBack', option: 'tagOption', out: 'tagOut' }[m.stock]];
      if (m.stock === 'out' && alertable) earnBlock += '<p class="kkwl-hint" data-kkwl-alert-hint>' + esc(m.alert ? opts.v11Copy.hintOn : opts.v11Copy.hintOff) + '</p>';
      s.tagSale = false;
      s.badge = '';
      s.note = '';
      if (m.alts.length) {
        earnBlock += '<div class="kkwl-alts" data-kkwl-alts hidden><p class="kkwl-alts__hint">' + esc(fill(opts.v11Copy.altHint, '[option]', m.variantTitle || m.title)) + '</p>' +
          m.alts.map(function (a) { return '<button type="button" class="kkwl-alt" data-action="pick-alt" data-vid="' + esc(a.id) + '" data-label="' + esc(a.label) + '">' + esc(a.label) + '</button>'; }).join('') + '</div>';
      }
    }

    return K.html({
      url: m.url,
      title: s.title,
      linkTitle: m.title,
      variantLabel: m.variantTitle,
      choices: m.choices,
      imgSrc: m.img ? imgUrl(m.img, isHero ? 900 : 600) : '',
      imgAlt: m.imgAlt,
      mediaExtraHTML: check,
      heartHTML: heartHTML,
      tag: s.tag,
      tagSale: s.tagSale,
      badge: s.badge,
      edition: edition.label,
      editionStyle: edition.style,
      showRating: opts.showRating,
      ratingValue: m.ratingValue,
      ratingCount: m.ratingCount,
      note: s.note,
      notePre: s.notePre,
      saved: m.saved,
      bodyExtraHTML: (bought ? boughtRowHTML(m.title, m.boughtAt, opts.boughtCopy) : '') + earnBlock,
      priceLabel: s.priceLabel,
      priceAmount: s.priceAmount,
      priceSale: s.priceSale,
      priceCompare: s.priceCompare,
      priceSave: s.priceSave,
      ctaHTML: cta,
      oos: !m.available,
      rootClass: 'kk-pc--wl' + (isHero ? ' kk-pc--hero' : '') + (v11 ? ' kk-pc--stock-' + m.stock : '') + (bought ? ' kk-pc--bought' : ''),
      rootAttrs: {
        'data-wl-item-id': m.itemId,
        // When it was bought (ms): present only on a bought card. The sort and the in-place checks read it.
        'data-kkwl-bought-at': bought ? m.boughtAt : null,
        'data-product-id': m.productId,
        'data-variant-id': m.variantId,
        // A configured bundle's saved choices — replayed by cartItemFor() on add.
        'data-properties': m.properties ? JSON.stringify(m.properties) : null,
        'data-handle': m.handle,
        'data-multi-variant': m.multiVariant ? 'true' : 'false',
        'data-on-sale': s.onSale ? 'true' : 'false',
        'data-available': m.available ? 'true' : 'false',
        'data-kkwl-title': m.title,
        // Search index (kkumi-wishlist-search.js) — the title/variant are read
        // from the card body; these carry the fields the card doesn't print.
        'data-price-cents': m.priceCents,
        'data-kkwl-type': m.type,
        'data-kkwl-vendor': m.vendor,
        'data-kkwl-tags': m.tags
      }
    });
  }

  var BELL = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9"/><path d="M10.3 21a1.94 1.94 0 0 0 3.4 0"/></svg>';
  function alertButton(on, opts) {
    return '<button type="button" class="button ' + (on ? 'button--secondary is-alert-on' : 'button--primary') + ' kk-pc__btn kkwl-alert" data-action="alert" role="switch" aria-checked="' + (on ? 'true' : 'false') + '">' +
      BELL + '<span>' + esc(on ? opts.v11Copy.alertOn : window.KKCard.copy.ctaNotify) + '</span></button>';
  }

  /* ---------------- render ---------------- */
  function optsFor(root) {
    return {
      limit: parseInt(root.getAttribute('data-kkwl-limit'), 10) || 0,
      noSelect: root.getAttribute('data-kkwl-no-select') === 'true',
      featured: root.getAttribute('data-kkwl-featured') === 'true',
      showRating: root.getAttribute('data-kkwl-show-rating') !== 'false',
      removeLabel: root.getAttribute('data-kkwl-remove') || 'Remove from wishlist',
      showPoints: root.getAttribute('data-kkwl-show-points') === 'true',
      pointsPattern: root.getAttribute('data-kkwl-points') || 'Earn [points] points',
      v11: root.getAttribute('data-kkwl-v11') === 'true',
      // Bought items (the section's "Bought items" settings). Off unless the root says so, so a host
      // that doesn't set it behaves exactly as before.
      bought: root.getAttribute('data-kkwl-bought') === 'true',
      boughtCopy: {
        label: root.getAttribute('data-kkwl-bought-label') || 'Bought [date]',
        remove: root.getAttribute('data-kkwl-bought-remove') || 'Remove',
        removing: root.getAttribute('data-kkwl-bought-removing') || 'Removing…',
        error: root.getAttribute('data-kkwl-bought-error') || 'We couldn\'t remove it. Please try again.'
      },
      v11Copy: {
        tagIn: root.getAttribute('data-kkwl-tag-in') || 'In stock',
        tagOut: root.getAttribute('data-kkwl-tag-out') || 'Sold out',
        tagOption: root.getAttribute('data-kkwl-tag-option') || 'Option sold out',
        chooseAlt: root.getAttribute('data-kkwl-choose-alt') || 'Choose another option',
        altHint: root.getAttribute('data-kkwl-alt-hint') || '[option] is sold out. These are still available:',
        inCart: root.getAttribute('data-kkwl-in-cart') || 'In cart',
        tagBack: root.getAttribute('data-kkwl-tag-back') || 'Back in stock',
        alertOn: root.getAttribute('data-kkwl-alert-on') || 'Alert on',
        hintOn: root.getAttribute('data-kkwl-hint-on') || 'We\'ll email you when it\'s back.',
        hintOff: root.getAttribute('data-kkwl-hint-off') || 'Turn on alerts and we\'ll email you when it\'s back',
        alertErr: root.getAttribute('data-kkwl-alert-err') || 'We couldn\'t toggle your alerts. Please try again.'
      }
    };
  }
  function realGrid(root) { return root.querySelector('.kkwl-grid:not(.kkwl-grid__skeleton)'); }
  // Register any earn-points trackers in freshly-rendered cards (no-op until
  // kkumi-earn-points.js is on the page, which the section loads when enabled).
  function wireEarn(grid, opts) {
    if (opts.showPoints && window.KkumiEarnPoints) {
      try { window.KkumiEarnPoints.scan(grid); } catch (e) {}
    }
  }
  function cardHTML(opts, item, product, idx, isHero) {
    var m = cardModel(item, product, idx);
    return gridCard(m, opts, isHero);
  }

  // Items per page when the full grid paginates (matches the server-rendered
  // skeleton count in kkumi-wishlist-grid.liquid — keep the two in sync).
  var PAGE_SIZE = 12;

  // Render resolved items into `grid`. A capped grid (data-kkwl-limit, e.g. the
  // dashboard preview) stays a single grid; the full grid paginates by
  // PAGE_SIZE — each page is its own .kkwl-grid--grid so the CSS grid survives,
  // and every card stays in the DOM (pages just toggle `hidden`) so the
  // toolbar's select-all / add-all / remove keep working across pages.
  function paginateGrid(grid, resolved, opts, productMap) {
    resolved = boughtLast(resolved, opts); // bought items go behind every item not bought, pages included
    var card = function (it, i) { return cardHTML(opts, it, productMap[it.productId], i, opts.featured && i === 0); };
    if (opts.limit) {
      resolved = resolved.slice(0, opts.limit);
      grid.className = 'kkwl-grid kkwl-grid--grid';
      grid.removeAttribute('data-kk-active-page');
      grid.innerHTML = resolved.map(card).join('');
      return;
    }
    var pageCount = Math.ceil(resolved.length / PAGE_SIZE);
    if (pageCount <= 1) {
      grid.className = 'kkwl-grid kkwl-grid--grid';
      grid.removeAttribute('data-kk-active-page');
      grid.innerHTML = resolved.map(card).join('');
      return;
    }
    grid.className = 'kkwl-grid kkwl-grid--pager';
    grid.setAttribute('data-kk-active-page', '0');
    var html = '';
    for (var p = 0; p < pageCount; p++) {
      html += '<div class="kkwl-grid--grid" data-kk-page="' + p + '"' + (p > 0 ? ' hidden' : '') + '>';
      for (var j = 0; j < PAGE_SIZE; j++) {
        var idx = p * PAGE_SIZE + j;
        if (idx >= resolved.length) { break; }
        html += card(resolved[idx], idx);
      }
      html += '</div>';
    }
    html += window.KKPager ? window.KKPager.navHTML(pageCount, 0) : '';
    grid.innerHTML = html;
    if (window.KKPager) { window.KKPager.mount(grid); }
  }

  function renderInto(root, items, productMap) {
    var grid = realGrid(root);
    if (!grid) { return; }
    var opts = optsFor(root);
    var resolved = items.filter(function (it) { return productMap[it.productId]; });
    if (!resolved.length) { return; }
    paginateGrid(grid, resolved, opts, productMap);
    stampOrder(grid, true);
    wireEarn(grid, opts);
    if (search.parsed && !opts.limit) applySearch();
  }


  /* ---------------- live search (input lives in the toolbar) ---------------- *
   * The toolbar calls KkwlBus.search(query, keywords) as the shopper types.
   * Matching/ranking is kkumi-wishlist-search.js (window.KkwlSearch); this
   * part only rearranges the cards already in the DOM — no refetch, no reload,
   * images stay put. Hits are re-paginated in rank order; misses move into a
   * hidden stash inside the grid, so selection / add-all / remove (which query
   * [data-wl-item-id] page-wide) still see every saved item. Capped grids (the
   * account dashboard preview) are never searched. */
  var search = { query: '', parsed: null, keywords: {} };
  function gridCards(grid) { return [].slice.call(grid.querySelectorAll('.kk-pc--wl:not(.kk-pc--ghost)')); }
  function ord(c) { return parseInt(c.getAttribute('data-kkwl-ord'), 10) || 0; }
  // A bought card goes behind every card not bought, before search rank and saved order are looked at.
  function byBought(a, b) { return (a.hasAttribute('data-kkwl-bought-at') ? 1 : 0) - (b.hasAttribute('data-kkwl-bought-at') ? 1 : 0); }
  // Remember each card's natural (saved) order so clearing a search restores it.
  function stampOrder(grid, reset) {
    var cards = gridCards(grid), max = -1;
    if (!reset) cards.forEach(function (c) { if (c.hasAttribute('data-kkwl-ord')) max = Math.max(max, ord(c)); });
    cards.forEach(function (c, i) {
      if (reset) c.setAttribute('data-kkwl-ord', String(i));
      else if (!c.hasAttribute('data-kkwl-ord')) c.setAttribute('data-kkwl-ord', String(++max));
    });
  }
  // Search record built from the card itself, so the editor's demo cards
  // (Liquid-rendered, no JS model) are searchable too.
  function cardRecord(card) {
    if (card._kkwlRec) return card._kkwlRec;
    var name = card.querySelector('.kk-pc__name a');
    var variant = card.querySelector('.kk-pc__variant');
    var cents = parseInt(card.getAttribute('data-price-cents'), 10);
    card._kkwlTitle = name ? name.textContent : '';
    card._kkwlRec = {
      title: card._kkwlTitle,
      variant: variant ? variant.textContent : '',
      vendor: card.getAttribute('data-kkwl-vendor') || '',
      type: card.getAttribute('data-kkwl-type') || '',
      tags: card.getAttribute('data-kkwl-tags') || '',
      priceCents: cents > 0 ? cents : null,
      available: card.getAttribute('data-available') !== 'false',
      onSale: card.getAttribute('data-on-sale') === 'true'
    };
    return card._kkwlRec;
  }
  function layoutCards(grid, hits, rest) {
    hits.concat(rest).forEach(function (c) { if (c.parentNode) c.parentNode.removeChild(c); });
    grid.innerHTML = '';
    var pageCount = Math.ceil(hits.length / PAGE_SIZE);
    if (pageCount <= 1) {
      grid.className = 'kkwl-grid kkwl-grid--grid';
      grid.removeAttribute('data-kk-active-page');
      hits.forEach(function (c) { grid.appendChild(c); });
    } else {
      grid.className = 'kkwl-grid kkwl-grid--pager';
      grid.setAttribute('data-kk-active-page', '0');
      for (var p = 0; p < pageCount; p++) {
        var page = document.createElement('div');
        page.className = 'kkwl-grid--grid';
        page.setAttribute('data-kk-page', String(p));
        if (p > 0) page.hidden = true;
        hits.slice(p * PAGE_SIZE, (p + 1) * PAGE_SIZE).forEach(function (c) { page.appendChild(c); });
        grid.appendChild(page);
      }
      if (window.KKPager) { grid.insertAdjacentHTML('beforeend', window.KKPager.navHTML(pageCount, 0)); window.KKPager.mount(grid); }
    }
    if (rest.length) {
      var stash = document.createElement('div');
      stash.className = 'kkwl-grid__stash';
      stash.hidden = true;
      rest.forEach(function (c) { stash.appendChild(c); });
      grid.appendChild(stash);
    }
  }
  function showNoResults(root, on) {
    var box = root.querySelector('[data-kkwl-noresults]');
    if (!box) return;
    if (on) {
      var title = box.querySelector('[data-kkwl-noresults-title]');
      var tpl = box.getAttribute('data-kkwl-noresults-tpl');
      // The template is merchant richtext (trusted); only the query is escaped.
      if (title && tpl) title.innerHTML = fill(tpl, '[query]', esc(search.query.trim()));
    }
    box.hidden = !on;
  }
  function applySearch() {
    if (!window.KkwlSearch) return;
    var parsed = search.parsed;
    var active = !!(parsed && !parsed.empty);
    var matches = 0, total = 0;
    roots.forEach(function (root) {
      if (optsFor(root).limit) return;
      var grid = realGrid(root);
      if (!grid) return;
      stampOrder(grid, false);
      var cards = gridCards(grid).sort(function (a, b) { return byBought(a, b) || (ord(a) - ord(b)); });
      var hits = [], rest = [];
      cards.forEach(function (c) {
        var rec = cardRecord(c);
        var sc = active ? window.KkwlSearch.score(rec, parsed) : 1;
        c._kkwlScore = sc;
        (sc > 0 ? hits : rest).push(c);
        var a = c.querySelector('.kk-pc__name a');
        if (a) a.innerHTML = (active && sc > 0) ? window.KkwlSearch.highlight(c._kkwlTitle, parsed) : esc(c._kkwlTitle);
      });
      if (active) hits.sort(function (a, b) { return byBought(a, b) || (b._kkwlScore - a._kkwlScore) || (ord(a) - ord(b)); });
      if (cards.length) layoutCards(grid, hits, rest);
      root.classList.toggle('kkwl-searching', active);
      showNoResults(root, active && cards.length > 0 && !hits.length);
      matches += hits.length;
      total += cards.length;
    });
    if (controller) controller._visibleChanged();
    if (window.KkwlBus) window.KkwlBus.emit('search', { query: search.query, active: active, matches: matches, total: total });
  }
  function runSearch(query, keywords) {
    if (!window.KkwlSearch) return;
    if (keywords) search.keywords = keywords;
    search.query = String(query == null ? '' : query);
    search.parsed = window.KkwlSearch.parse(search.query, search.keywords);
    applySearch();
  }
  if (window.KkwlBus && window.KkwlSearch) {
    window.KkwlBus.search = runSearch;
    // A ?q= deep link the toolbar parsed before this script ran.
    var pending = window.KkwlBus.pendingSearch;
    if (pending) { window.KkwlBus.pendingSearch = null; runSearch(pending.query, pending.keywords); }
    // "Clear search" in the no-results panel.
    roots.forEach(function (root) {
      root.addEventListener('click', function (e) {
        if (e.target.closest && e.target.closest('[data-action="clear-search"]')) { e.preventDefault(); runSearch(''); }
      });
    });
  }

  /* ---------------- selection controller (shared with the toolbar via KkwlBus) ---------------- */
  var controller = null;
  var designMode = !!(window.Shopify && window.Shopify.designMode);
  var loggedIn = roots[0] ? roots[0].getAttribute('data-kkwl-logged-in') !== 'false' : true;
  function allCards() { return document.querySelectorAll('[data-wl-item-id]'); }
  // Cards the shopper can currently see (i.e. not filtered out by a search).
  function visibleCards() {
    return Array.prototype.filter.call(allCards(), function (c) { return !c.closest('.kkwl-grid__stash'); });
  }

  // Club members only (owner, 28 Oct 2026; the Wishlist app's setting, core: WK.club): a signed-in shopper the app says
  // hasn't joined the Kkumi Club sees the Club join card (data-kkwl-state="club") in place of the empty state. A page
  // without that card keeps the empty state, so nothing is ever left blank.
  function clubGate() {
    var WK = window.WishlistKing;
    return !!(WK && WK.club && WK.club.blocked() && document.querySelector('[data-kkwl-state="club"]'));
  }
  function emptyStatus() { return clubGate() ? 'club' : 'empty'; }

  // Reveal the page section matching the wishlist status (guest / club / empty / populated).
  // Sections elsewhere on the page mark themselves with data-kkwl-state.
  function applyStatus(status) {
    if (designMode) return; // editor: show everything so each section is editable
    roots.forEach(function (r) { r.classList.toggle('kkwl-hidden', status !== 'populated'); });
    document.querySelectorAll('[data-kkwl-bar]').forEach(function (el) { el.classList.remove('kkwl-prehidden'); el.classList.toggle('kkwl-hidden', status !== 'populated'); });
    document.querySelectorAll('[data-kkwl-state]').forEach(function (el) {
      var match = el.getAttribute('data-kkwl-state') === status;
      el.classList.remove('kkwl-prehidden');
      el.classList.toggle('kkwl-hidden', !match);
    });
  }

  function buildController() {
    var selected = {};

    function applyCard(card) {
      var on = !!selected[card.getAttribute('data-wl-item-id')];
      card.classList.toggle('is-selected', on);
      var chk = card.querySelector('[data-action="select"]');
      if (chk) chk.setAttribute('aria-pressed', on ? 'true' : 'false');
    }
    function refreshAll() { allCards().forEach(applyCard); }
    function ids() { return Object.keys(selected); }
    // Eligible to add to cart: in stock AND carrying a concrete saved variant.
    // Wishlist saves are variant-level (each item stores the exact variantId the
    // customer saved), so multi-variant products ARE addable — we add the saved
    // variant. Only items without a real variant id (legacy product-level saves)
    // are skipped, since there's nothing to put in the cart.
    function eligible(card) {
      return card.getAttribute('data-available') !== 'false' && Number(card.getAttribute('data-variant-id')) > 0
        // Gift mode: "Add all" / "Add selected" leave out what someone else is already getting (the card's own button still adds it).
        && !card.classList.contains('is-gift-taken');
    }
    function state() {
      var inStock = 0, outStock = 0, sale = 0;
      allCards().forEach(function (c) {
        if (c.getAttribute('data-available') === 'false') outStock++; else inStock++;
        if (c.getAttribute('data-on-sale') === 'true') sale++;
      });
      var total = inStock + outStock;
      // total / inStock / outStock / sale always describe the whole list (the
      // hero chips read them); a search narrows only `matches` + selection.
      var vis = visibleCards();
      var allSel = vis.length > 0 && vis.every(function (c) { return selected[c.getAttribute('data-wl-item-id')]; });
      var searching = !!(search.parsed && !search.parsed.empty);
      return { total: total, selected: ids().length, allSelected: allSel, inStock: inStock, outStock: outStock, sale: sale, searching: searching, matches: vis.length };
    }
    function notify() {
      var st = state();
      if (window.KkwlBus) KkwlBus.emit('change', st);
      if (loggedIn && st.total === 0) applyStatus(emptyStatus()); // emptied the list -> show empty state (or the Club join card)
    }
    function selectWhere(test) {
      // Replace mode: clear the prior selection first, then pick whatever
      // matches. Makes the "Select in stock" / "Select out of stock"
      // pickers mutually exclusive (clicking one un-checks anything the
      // other picked), and the same one twice in a row is a no-op.
      selected = {};
      visibleCards().forEach(function (c) { if (test(c)) selected[c.getAttribute('data-wl-item-id')] = true; });
      refreshAll(); notify();
    }
    function collect(fromSelected) {
      var items = [], skipped = 0;
      var pool = fromSelected
        ? ids().map(function (id) { return document.querySelector('[data-wl-item-id="' + id + '"]'); })
        : visibleCards(); // "Add all" = everything the shopper can see
      pool.forEach(function (c) {
        if (!c) return;
        if (!eligible(c)) { skipped++; return; }
        var vid = Number(c.getAttribute('data-variant-id'));
        if (vid) items.push(cartItemFor(c, vid));
      });
      return items;
    }

    return {
      state: state,
      toggle: function (id) {
        if (selected[id]) delete selected[id]; else selected[id] = true;
        var card = document.querySelector('[data-wl-item-id="' + id + '"]');
        if (card) applyCard(card);
        notify();
      },
      selectAll: function () { selectWhere(function () { return true; }); },
      selectInStock: function () { selectWhere(function (c) { return c.getAttribute('data-available') !== 'false'; }); },
      selectOutOfStock: function () { selectWhere(function (c) { return c.getAttribute('data-available') === 'false'; }); },
      clear: function () { selected = {}; refreshAll(); notify(); },
      removeSelected: function () {
        var list = ids();
        list.forEach(function (id) {
          var card = document.querySelector('[data-wl-item-id="' + id + '"]');
          delete selected[id];
          if (card) removeItem(card);
        });
        notify();
      },
      addAllToCart: function (btn) {
        if (optsFor(roots[0]).v11) { var all = visibleCards().filter(eligible); if (all.length) addEach(all, btn); return; }
        var items = collect(false); if (items.length) addItemsToCart(items, btn);
      },
      addSelectedToCart: function (btn) {
        if (optsFor(roots[0]).v11) {
          var sel = ids().map(function (id) { return document.querySelector('[data-wl-item-id="' + id + '"]'); }).filter(function (c) { return c && eligible(c); });
          if (sel.length) addEach(sel, btn);
          return;
        }
        var items = collect(true); if (items.length) addItemsToCart(items, btn);
      },
      // v11: cards in the cart / addable, for the header's "Add [n] available".
      addableCount: function () { return visibleCards().filter(eligible).length; },
      eligibleSelected: function () { return ids().filter(function (id) { var c = document.querySelector('[data-wl-item-id="' + id + '"]'); return c && eligible(c); }).length; },
      getShareUrl: function () {
        if (!shareMeta || !shareMeta.publicId) return null;
        // Our on-brand read-only view lives on this page at ?shared={publicId}.
        // Carry the owner's first name (?by=) so the shared page can greet them;
        // omitted for guests (no account) -> the shared view uses its fallback.
        var owner = (roots[0] && roots[0].getAttribute('data-kkwl-owner')) || '';
        var url = window.location.origin + window.location.pathname + '?shared=' + shareMeta.publicId;
        return owner ? url + '&by=' + encodeURIComponent(owner) : url;
      },
      _changed: notify,
      // A search hid some cards: drop them from the selection so "Remove
      // selected" can never act on items the shopper can't see.
      _visibleChanged: function () {
        var keep = {};
        visibleCards().forEach(function (c) { var id = c.getAttribute('data-wl-item-id'); if (selected[id]) keep[id] = true; });
        selected = keep;
        refreshAll(); notify();
      }
    };
  }

  /* ---------------- actions ---------------- */
  function bindActions(root) {
    root.addEventListener('click', function (e) {
      var sel = e.target.closest('[data-action="select"]');
      var rm = e.target.closest('[data-action="remove"]');
      var rb = e.target.closest('[data-action="remove-bought"]');
      var ac = e.target.closest('[data-action="addcart"]');
      if (sel) { e.preventDefault(); var sc = sel.closest('[data-wl-item-id]'); if (sc && controller) controller.toggle(sc.getAttribute('data-wl-item-id')); return; }
      // A bought card's own Remove button: waits for the Worker (see removeBought).
      if (rb) { e.preventDefault(); removeBought(rb.closest('[data-wl-item-id]'), rb, optsFor(root)); return; }
      // stopPropagation on remove: the sitewide kkumi-wishlist.js delegates a
      // heart-click handler on document that would also call removeWishlistItem
      // (double DELETE + double Klaviyo event). The grid owns removal here.
      if (rm) { e.preventDefault(); e.stopPropagation(); removeItem(rm.closest('[data-wl-item-id]')); return; }
      if (ac) { e.preventDefault(); addToCart(ac.closest('[data-wl-item-id]'), ac); return; }
      var alt = e.target.closest('[data-action="alt"]');
      if (alt) {
        e.preventDefault();
        var box = alt.closest('[data-wl-item-id]').querySelector('[data-kkwl-alts]');
        if (box) { box.hidden = !box.hidden; alt.setAttribute('aria-expanded', String(!box.hidden)); if (!box.hidden) { var f = box.querySelector('button'); if (f) f.focus(); } }
        return;
      }
      var al = e.target.closest('[data-action="alert"]');
      if (al) { e.preventDefault(); toggleAlert(al.closest('[data-wl-item-id]'), al, optsFor(root)); return; }
      var pick = e.target.closest('[data-action="pick-alt"]');
      if (pick) { e.preventDefault(); pickAlt(pick.closest('[data-wl-item-id]'), pick, optsFor(root)); }
    });
  }

  // v11: the saved option is sold out and the shopper picked another one —
  // the card now adds that variant (this visit only; the saved item is kept).
  function pickAlt(card, btn, opts) {
    if (!card) return;
    card.setAttribute('data-variant-id', btn.getAttribute('data-vid'));
    card.setAttribute('data-available', 'true');
    card.classList.remove('kk-pc--stock-option', 'kk-pc--oos');
    card.classList.add('kk-pc--stock-in');
    var v = card.querySelector('.kk-pc__variant:not(.kk-pc__choices)');
    if (v) v.textContent = btn.getAttribute('data-label');
    var tag = card.querySelector('.kk-pc__tag');
    if (tag) tag.textContent = opts.v11Copy.tagIn;
    var box = card.querySelector('[data-kkwl-alts]');
    if (box) box.parentNode.removeChild(box);
    var cta = card.querySelector('.kk-pc__cta');
    if (cta) {
      cta.innerHTML = window.KKCard.button({ label: window.KKCard.copy.ctaAdd, attrs: { 'data-action': 'addcart' } });
      var b = cta.querySelector('button'); if (b) b.focus();
    }
    if (controller) controller._changed();
  }

  // v11: switch a sold-out item's back-in-stock alert (optimistic; reverts
  // and says so if the save fails).
  function toggleAlert(card, btn, opts) {
    var WK = window.WishlistKing;
    if (!card || !WK || !WK.setItemAlert || btn.getAttribute('aria-busy') === 'true') return;
    var next = btn.getAttribute('aria-checked') !== 'true';
    var hint = card.querySelector('[data-kkwl-alert-hint]');
    var paint = function (on) {
      var wrap = btn.parentNode;
      wrap.innerHTML = alertButton(on, opts);
      btn = wrap.querySelector('[data-action="alert"]');
      if (hint) { hint.textContent = on ? opts.v11Copy.hintOn : opts.v11Copy.hintOff; hint.classList.remove('is-err'); }
      return btn;
    };
    var nb = paint(next);
    nb.setAttribute('aria-busy', 'true');
    nb.focus();
    WK.setItemAlert(card.getAttribute('data-wl-item-id'), next).then(function () {
      nb.removeAttribute('aria-busy');
    }, function () {
      var back = paint(!next);
      back.focus();
      if (hint) { hint.textContent = opts.v11Copy.alertErr; hint.classList.add('is-err'); }
    });
  }

  // v11: a card whose item went into the cart says so ("In cart ✓").
  function markInCart(card) {
    var opts = optsFor(card.closest('[data-kkwl-grid]') || roots[0]);
    if (!opts.v11) return;
    var b = card.querySelector('[data-action="addcart"]');
    if (!b) return;
    b.className = 'button button--secondary kk-pc__btn is-in-cart';
    b.innerHTML = SVG.check + '<span>' + esc(opts.v11Copy.inCart) + '</span>';
    card.setAttribute('data-in-cart', 'true');
  }

  // What WishlistKing.removeWishlistItem needs to find the item, read off its card.
  function removalPayload(card) {
    return {
      productHandle: card.getAttribute('data-handle'),
      wishlistItemId: card.getAttribute('data-wl-item-id'),
      productId: Number(card.getAttribute('data-product-id')),
      variantId: Number(card.getAttribute('data-variant-id')),
      inWishlist: true
    };
  }
  // Tell the other wishlist sections an item is gone (the account tab's Undo toast listens). Called while the card is
  // still on the page. undo() puts it back (restoreCard); commit() lets a held removal go to the Worker.
  function announceRemoved(card, payload, hold) {
    if (!window.KkwlBus) return;
    var raw = card.getAttribute('data-properties'), props = null;
    try { props = raw ? JSON.parse(raw) : null; } catch (e) {}
    var spot = spotOf(card);
    KkwlBus.emit('removed', {
      productId: payload.productId, variantId: payload.variantId, productHandle: payload.productHandle, properties: props, title: card.getAttribute('data-kkwl-title') || '',
      undo: function () { return restoreCard(card, spot, payload, props, hold); },
      commit: function () { if (hold) hold.commit(); }
    });
  }
  // Fade the card out and take it off the page; `after` runs once it is gone.
  function dropCard(card, after) {
    card.classList.add('is-removing');
    card.style.transition = 'opacity .2s ease, transform .2s ease';
    card.style.opacity = '0';
    card.style.transform = 'scale(0.95)';
    clearTimeout(card._kkwlDrop);
    card._kkwlDrop = setTimeout(function () {
      card._kkwlDrop = null;
      card.parentNode && card.parentNode.removeChild(card);
      if (search.parsed && !search.parsed.empty) applySearch(); // re-paginate hits / show "no matches"
      if (controller) controller._changed();
      if (after) after();
    }, 200);
  }

  function removeItem(card) {
    if (!card) return;
    var WK = window.WishlistKing;
    var payload = removalPayload(card);
    // With an Undo toast on the page (the account tab sets KkwlBus.offersUndo) the removal waits for the toast to go,
    // so Undo can keep the very same item. Elsewhere the Worker hears at once, as before.
    var hold = null;
    try {
      if (WK && WK.removeWithUndo && window.KkwlBus && KkwlBus.offersUndo) hold = WK.removeWithUndo(payload);
      else if (WK) WK.removeWishlistItem(payload);
    } catch (e) {}
    announceRemoved(card, payload, hold);
    dropCard(card);
  }

  // Where a card sat: the page order of every card when it went (across pages and the search stash), so Undo finds its
  // place again even when its neighbours went with it or the grid has been laid out afresh.
  function spotOf(card) {
    var root = card.closest('[data-kkwl-grid]') || roots[0];
    var grid = realGrid(root);
    var ids = (grid ? gridCards(grid) : []).map(function (c) { return c.getAttribute('data-wl-item-id'); });
    return { root: root, ids: ids, at: ids.indexOf(card.getAttribute('data-wl-item-id')) };
  }
  // In front of the next card in that order still on the page, else after the last one before it, else first.
  function placeCard(card, spot) {
    var grid = realGrid(spot.root);
    if (!grid) return;
    var byId = {};
    gridCards(grid).forEach(function (c) { byId[c.getAttribute('data-wl-item-id')] = c; });
    for (var i = spot.at + 1; i < spot.ids.length; i++) {
      if (byId[spot.ids[i]]) { byId[spot.ids[i]].parentNode.insertBefore(card, byId[spot.ids[i]]); return; }
    }
    for (var j = spot.at - 1; j >= 0; j--) {
      if (byId[spot.ids[j]]) { byId[spot.ids[j]].parentNode.insertBefore(card, byId[spot.ids[j]].nextSibling); return; }
    }
    var first = grid.querySelector('[data-kk-page]') || grid;
    first.insertBefore(card, first.firstChild);
  }

  // Undo (the account tab's toast): the card goes back where it was, at once. A held removal is simply called off:
  // nothing reached the Worker, so the very same item is still saved (date, alert, bought mark). A removal the Worker
  // already has (a bought card's Remove, or a held one already sent) is saved again once that request is done, so the
  // two can't cross; the card takes the new row's id and its alert switch is set back the way it was. If that save
  // fails the card goes again and the promise rejects (the toast says so).
  function restoreCard(card, spot, payload, props, hold) {
    clearTimeout(card._kkwlDrop);
    card._kkwlDrop = null;
    card.classList.remove('is-removing');
    card.style.transition = card.style.opacity = card.style.transform = '';
    if (!card.isConnected) placeCard(card, spot);
    var kept = !!(hold && hold.undo());
    if (!kept) card.setAttribute('data-kkwl-restoring', 'true'); // resync leaves it alone until its new row lands
    if (search.parsed && !search.parsed.empty) applySearch();
    applyStatus('populated'); // it may have been the last card
    if (controller) controller._visibleChanged(); // no stale tick from Select mode; counts follow
    if (kept) return Promise.resolve();
    var WK = window.WishlistKing;
    var alert = card.querySelector('[data-action="alert"]');
    var alertOff = !!alert && alert.getAttribute('aria-checked') === 'false';
    return Promise.resolve(hold && hold.done).then(function () {
      if (!WK) throw new Error('unavailable');
      return WK.addWishlistItem({ productId: payload.productId, variantId: payload.variantId || null, productHandle: payload.productHandle, properties: props || undefined, restore: true });
    }).then(function (res) {
      var id = res && res.item && res.item.id;
      if (id) card.setAttribute('data-wl-item-id', id);
      card.removeAttribute('data-kkwl-restoring');
      if (id && alertOff && WK.setItemAlert) WK.setItemAlert(id, false).then(null, function () {});
    }, function (err) {
      card.removeAttribute('data-kkwl-restoring');
      dropCard(card);
      throw err;
    });
  }

  // A bought card's Remove button. Unlike the heart (optimistic, fire and forget) this one waits
  // for the Worker — WishlistKing.removeWishlistItem in its confirm form — and the card goes only
  // once the item is really gone. On a failure the card stays, the button is restored and the card
  // says so (the section's "couldn't remove" line). The busy state is lifted on success and failure.
  function removeBought(card, btn, opts) {
    if (!card || !btn || btn.getAttribute('aria-busy') === 'true') return;
    var WK = window.WishlistKing;
    var row = btn.closest('[data-kkwl-bought-row]');
    var stale = row && row.parentNode && row.parentNode.querySelector('.kkwl-bought__err');
    if (stale) stale.parentNode.removeChild(stale); // a retry starts clean
    var payload = removalPayload(card);
    payload.variantId = payload.variantId || null; // "no variant" is null in the list, not 0 (Number() of nothing)
    // Where focus goes when this card disappears under a keyboard user: the next card, else the one before.
    var heir = card.nextElementSibling || card.previousElementSibling;
    var restore = function () { setLoading(btn, false); };
    setLoading(btn, true, opts.boughtCopy.removing);
    var done;
    try { done = WK ? WK.removeWishlistItem(payload, { confirm: true }) : Promise.reject(new Error('unavailable')); } catch (e) { done = Promise.reject(e); }
    Promise.resolve(done).then(function () {
      try {
        announceRemoved(card, payload);
        dropCard(card, function () {
          restore();
          var link = heir && heir.parentNode && heir.querySelector('.kk-pc__link');
          if (link) link.focus({ preventScroll: true });
        });
      } catch (e) { restore(); }
    }, function () {
      restore();
      if (row) row.insertAdjacentHTML('afterend', '<p class="kkwl-bought__err" role="alert">' + esc(opts.boughtCopy.error) + '</p>');
    });
  }

  // Uniform busy state: spinner ring + "Adding…" label via window.KKBtn — the
  // same treatment the grid product cards (global.js) and every other async
  // button get. (The card's old label-less .loading__spinner overlay was removed
  // from kkumi-product-card-v3.css, so KKBtn's ring now shows through.)
  function setLoading(btn, on, label) {
    if (!btn) return;
    if (on) {
      btn.classList.add('is-loading');
      if (window.KKBtn) window.KKBtn.busy(btn, label || LABELS.adding);
      else { btn.disabled = true; btn.setAttribute('aria-busy', 'true'); }
    } else {
      btn.classList.remove('is-loading');
      if (window.KKBtn) window.KKBtn.reset(btn);
      else { btn.disabled = false; btn.removeAttribute('aria-busy'); }
    }
  }

  function refreshCartCount(rootUrl) {
    fetch(rootUrl + 'cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var count = cart.item_count;
        var bubble = document.getElementById('cart-icon-bubble');
        if (!bubble) return;
        var span = bubble.querySelector('.kkumi-bubble-count');
        if (span) {
          span.textContent = count > 99 ? '99+' : count;
          span.style.setProperty('display', count > 0 ? 'flex' : 'none', 'important');
        }
      })
      .catch(function (e) { });
  }

  function cartSections() {
    var cartDrawer = document.querySelector('cart-drawer');
    var sections = [];
    try {
      if (cartDrawer && cartDrawer.getSectionsToRender) {
        sections = cartDrawer.getSectionsToRender().map(function (s) { return s.section || s.id; }).filter(Boolean);
      }
    } catch (e) {}
    if (sections.indexOf('cart-icon-bubble') < 0) sections.push('cart-icon-bubble');
    return sections;
  }

  // v11 "Add all / selected": one /cart/add.js per item so a refused line
  // doesn't sink the rest; KkwlBus 'cartResult' { added, failed[{title,reason}] }
  // drives the header's result banner.
  function addEach(cards, btn) {
    var rootUrl = (window.Shopify && Shopify.routes && Shopify.routes.root) ? Shopify.routes.root : '/';
    setLoading(btn, true);
    var added = 0, failed = [];
    cards.reduce(function (p, c) {
      return p.then(function () {
        var item = cartItemFor(c, Number(c.getAttribute('data-variant-id')));
        return fetch(rootUrl + 'cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' }, body: JSON.stringify({ items: [item] }) })
          .then(function (r) { return r.json().then(function (b) { return { ok: r.ok && !b.status, b: b }; }, function () { return { ok: r.ok, b: {} }; }); })
          .then(function (res) {
            if (res.ok) { added++; markInCart(c); }
            else failed.push({ title: c.getAttribute('data-kkwl-title') || '', reason: (res.b && (res.b.description || res.b.message)) || '' });
          }, function () { failed.push({ title: c.getAttribute('data-kkwl-title') || '', reason: '' }); });
      });
    }, Promise.resolve()).then(function () {
      setLoading(btn, false);
      if (added) refreshCartCount(rootUrl);
      if (window.KkwlBus) KkwlBus.emit('cartResult', { added: added, failed: failed, cards: cards });
    });
  }

  function addItemsToCart(items, btn, card) {
    var cartDrawer = document.querySelector('cart-drawer');
    var rootUrl = (window.Shopify && Shopify.routes && Shopify.routes.root) ? Shopify.routes.root : '/';
    setLoading(btn, true);
    fetch(rootUrl + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/javascript' },
      body: JSON.stringify({ items: items, sections: cartSections(), sections_url: window.location.pathname })
    })
      .then(function (r) { return r.json(); })
      .then(function (parsed) {
        setLoading(btn, false);
        if (parsed.status) {
          if (window.KkwlBus) KkwlBus.emit('cartResult', { added: 0, failed: [{ title: card ? card.getAttribute('data-kkwl-title') || '' : '', reason: parsed.description || parsed.message || '' }], cards: card ? [card] : [] });
          return;
        }
        if (card) markInCart(card);
        if (cartDrawer && parsed.sections && cartDrawer.renderContents) {
          try { cartDrawer.renderContents(parsed); } catch (e) {}
        }
        refreshCartCount(rootUrl);
      })
      .catch(function (e) { setLoading(btn, false); });
  }

  // One /cart/add.js item for a card: its saved variant, plus any saved line
  // properties — a configured bundle's choices, without which the bundle is
  // added as a bare parent and checks out with no components.
  function cartItemFor(card, variantId) {
    var item = { id: variantId, quantity: 1 };
    var raw = card.getAttribute('data-properties');
    if (raw) {
      try {
        var props = JSON.parse(raw);
        if (props && typeof props === 'object' && !Array.isArray(props)) item.properties = props;
      } catch (e) {}
    }
    return item;
  }

  function addToCart(card, btn) {
    if (!card) return;
    var variantId = Number(card.getAttribute('data-variant-id'));
    addItemsToCart([cartItemFor(card, variantId)], btn, card);
  }

  /* ---------------- boot ---------------- */
  function removeLoading() { roots.forEach(function (r) { r.classList.remove('is-loading'); r.classList.remove('kkwl-gift-pending'); }); }
  function clearGrids() { roots.forEach(function (r) { var g = realGrid(r); if (g) g.innerHTML = ''; }); }

  // Re-sync the grid when the wishlist changes elsewhere on the page (e.g. a
  // product is saved via a recommendation/collection heart) — no reload.
  var resyncTimer;
  function scheduleResync(reason) { clearTimeout(resyncTimer); resyncTimer = setTimeout(function () { resync(reason); }, 900); }
  function resync(reason) {
    if (!loggedIn || !window.WishlistKing) return;
    fetchWishlist(window.WishlistKing).then(function (items) {
      var root = roots[0];
      var grid = realGrid(root);
      if (!grid) return;
      var rsOpts = optsFor(root);
      if (!items.length) {
        grid.innerHTML = '';
        applyStatus(emptyStatus());
        if (controller) controller._changed();
        return;
      }
      // A purchase the list only just revealed (the page painted from a copy cached before
      // the order): the whole grid is drawn again, so the tag and the order follow.
      var boughtShift = rsOpts.bought && (function () {
        var byId = {};
        grid.querySelectorAll('[data-wl-item-id]').forEach(function (c) { byId[c.getAttribute('data-wl-item-id')] = c; });
        return items.some(function (it) { var c = byId[it.id]; return !!c && (Number(it.purchasedAt) > 0) !== c.hasAttribute('data-kkwl-bought-at'); });
      })();
      // A paginated grid keeps cards inside per-page wrappers, so the
      // incremental append/remove below can't place them — re-render the whole
      // grid through renderInto (which re-paginates) instead. Also catches the
      // grid crossing the PAGE_SIZE boundary in either direction.
      if (!rsOpts.limit && (items.length > PAGE_SIZE || grid.classList.contains('kkwl-grid--pager') || boughtShift)) {
        resolveProducts(window.WishlistKing, items.map(function (it) { return it.productId; })).then(function (mapOrNull) {
          pruneZombies(window.WishlistKing, items, mapOrNull);
          renderInto(root, items, mapOrNull || {});
          var n = grid.querySelectorAll('[data-wl-item-id]').length;
          applyStatus(n ? 'populated' : emptyStatus());
          if (!controller) { controller = buildController(); if (window.KkwlBus) KkwlBus.setGrid(controller); }
          controller._changed();
        });
        return;
      }
      // Wishlist saves are variant-level — a customer can save multiple
      // variants of the same product as independent items, each with its own
      // card. Key on the wishlist-item id (or productId|variantId fallback)
      // so duplicate productIds don't collapse into a single entry.
      var keyOf = function (it) { return String(it.id || (it.productId + '|' + (it.variantId || 0))); };
      var cardKeyOf = function (c) { return c.getAttribute('data-wl-item-id') || (c.getAttribute('data-product-id') + '|' + (c.getAttribute('data-variant-id') || 0)); };
      var live = {};
      items.forEach(function (it) { live[keyOf(it)] = it; });
      // drop cards that are no longer wishlisted (not one Undo is saving again: its new row isn't in the list yet)
      Array.prototype.forEach.call(grid.querySelectorAll('[data-wl-item-id]'), function (card) {
        if (!live[cardKeyOf(card)] && !card.hasAttribute('data-kkwl-restoring')) { card.parentNode.removeChild(card); }
      });
      var present = {};
      Array.prototype.forEach.call(grid.querySelectorAll('[data-wl-item-id]'), function (c) { present[cardKeyOf(c)] = true; });
      var added = items.filter(function (it) { return !present[keyOf(it)]; });

      if (!items.length) { applyStatus(emptyStatus()); if (controller) controller._changed(); return; }

      // Status is decided by what's actually renderable: an item whose product
      // no longer resolves must not pin the page in the 'populated' state.
      var finish = function () {
        var cards = grid.querySelectorAll('[data-wl-item-id]').length;
        applyStatus(cards ? 'populated' : emptyStatus());
        if (!controller) { controller = buildController(); if (window.KkwlBus) KkwlBus.setGrid(controller); }
        if (search.parsed) applySearch();
        controller._changed();
      };
      if (!added.length) { finish(); return; }

      resolveProducts(window.WishlistKing, added.map(function (it) { return it.productId; })).then(function (mapOrNull) {
        var map = mapOrNull || {};
        pruneZombies(window.WishlistKing, added, mapOrNull);
        var opts = optsFor(root);
        added.forEach(function (it) {
          var p = map[it.productId];
          if (!p) return;
          if (opts.limit && grid.querySelectorAll('[data-wl-item-id]').length >= opts.limit) return;
          var html = cardHTML(opts, it, p, 0, false);
          // Bought cards stay last: a card not bought goes in front of the first bought one.
          var firstBought = opts.bought && !(Number(it.purchasedAt) > 0) && grid.querySelector(':scope > [data-kkwl-bought-at]');
          if (firstBought) firstBought.insertAdjacentHTML('beforebegin', html);
          else grid.insertAdjacentHTML('beforeend', html); // append so the existing hero (first card) keeps its position
        });
        finish();
      });
    }).catch(function (e) { });
  }
  function setupResync() {
    document.addEventListener('wk:countUpdated', function () { scheduleResync('wk:countUpdated'); });
    // Club members only: the app's answer changed (they joined, or the setting was switched), so show the right state.
    document.addEventListener('wk:clubUpdated', function () { scheduleResync('wk:clubUpdated'); });
    // Capture phase: kkumi-wishlist.js calls stopPropagation, so listen before it.
    document.addEventListener('click', function (e) {
      if (e.target && e.target.closest && e.target.closest('.kkumi-card__wish')) scheduleResync('save heart');
    }, true);
  }

  // In the theme editor, drop the skeleton immediately so the merchant sees the placeholder design.
  if (window.Shopify && window.Shopify.designMode) removeLoading();

  /* ---------------- shared (read-only) view ---------------- *
   * ?shared={publicId} renders someone else's public wishlist read-only:
   * no select / unsave; keep view + add-to-cart; no toolbar. The public-list
   * endpoint is a best guess pending confirmation — the response is logged.   */
  var SHARED_ID = (new URLSearchParams(window.location.search)).get('shared');
  function renderShared(root, items, map) {
    var grid = realGrid(root);
    if (!grid) return;
    var opts = optsFor(root);
    opts.readOnly = true;
    var resolved = items.filter(function (it) { return map[it.productId]; });
    paginateGrid(grid, resolved, opts, map);
    stampOrder(grid, true);
    if (search.parsed) applySearch();
  }
  // Gift mode (assets/kkumi-wishlist-gift.js, loaded by the section when its setting is on).
  function giftOn(root) { return !!window.KkwlGift && root.getAttribute('data-kkwl-gift-on') === 'true'; }
  function bootShared(publicId) {
    roots.forEach(function (r) { r.classList.add('kkwl-shared'); r.classList.remove('kkwl-hidden'); });
    roots.forEach(bindActions); // select + add-to-cart still work in the shared view
    // The loaded cards will carry a gift row, so the loading ghosts get a row's height too (no jump when the cards land).
    roots.forEach(function (r) { if (giftOn(r)) r.classList.add('kkwl-gift-pending'); });
    var bars = document.querySelectorAll('[data-kkwl-bar]');
    var revealBar = function () { Array.prototype.forEach.call(bars, function (el) { el.classList.remove('kkwl-prehidden'); el.classList.remove('kkwl-hidden'); }); };
    var hideBar = function () { Array.prototype.forEach.call(bars, function (el) { el.classList.add('kkwl-hidden'); el.classList.remove('kkwl-prehidden'); }); };
    waitForWK(function (WK) {
      var apiHost = WK.config.routes.apiHost;
      var headers = WK._apis.wishlist.headers;
      var url = apiHost + '/wishlists/' + publicId;
      fetch(url, { headers: headers })
        .then(function (r) { return r.json(); })
        .then(function (data) {
          var wl = (data && data.wishlist) || data || {};
          var items = wl.items || [];
          // The custom backend returns the owner's stored name; fall back to a
          // name carried in the link (?by=) for older shared URLs.
          var byParam = (new URLSearchParams(window.location.search)).get('by');
          var name = wl.ownerName || byParam || wl.customerName || wl.name || wl.title || (wl.customer && (wl.customer.firstName || wl.customer.name)) || '';
          if (window.KkwlBus) KkwlBus.setShared(name, items); // tell hero/recs/share it's a shared view
          if (!items.length) { hideBar(); removeLoading(); return; }
          resolveProducts(WK, items.map(function (i) { return i.productId; })).then(function (mapOrNull) {
            var map = mapOrNull || {}; // never prune someone else's shared list
            roots.forEach(function (root) { renderShared(root, items, map); });
            // Gift mode: the worker offers it per viewer (never to the list's owner; no `gift` in the reply = no gift UI).
            // A courtesy feature: whatever goes wrong here must never take the list down with it.
            roots.forEach(function (root) {
              if (!giftOn(root)) return;
              try {
                var g = window.KkwlGift.start({ root: root, publicId: publicId, apiHost: apiHost, items: items, offer: data && data.gift, ownerName: name });
                if (g) g.mount();
              } catch (e) {}
            });
            controller = buildController();
            if (window.KkwlBus) KkwlBus.setGrid(controller); // read-only here, but broadcasts counts for the hero chips
            revealBar(); // keep the bar in shared view (read-only); its ghost disappears
            removeLoading();
          });
        })
        .catch(function (e) { hideBar(); removeLoading(); });
    }, function () { hideBar(); removeLoading(); });
  }
  if (SHARED_ID) { bootShared(SHARED_ID); return; }

  waitForWK(function (WK) {
    roots.forEach(bindActions); // delegated handlers up front, so resync-added cards work even if the grid started empty
    setupResync();
    fetchWishlist(WK).then(function (items) {
      var populated = items.length > 0;
      var status = !loggedIn ? 'guest' : (populated ? 'populated' : emptyStatus());

      var done = function () {
        controller = buildController();
        if (window.KkwlBus) KkwlBus.setGrid(controller);
      };
      if (populated && (status === 'populated' || designMode)) {
        var ids = items.map(function (i) { return i.productId; });
        resolveProducts(WK, ids).then(function (mapOrNull) {
          var map = mapOrNull || {};
          pruneZombies(WK, items, mapOrNull);
          // An authoritative resolution with zero renderable products = the
          // list only held now-gone products: show the empty state, not a
          // bare grid. (On a failed resolution keep the old behaviour.)
          var resolved = items.filter(function (it) { return map[it.productId]; });
          if (mapOrNull && !resolved.length) {
            applyStatus(emptyStatus());
            clearGrids();
            done();
            removeLoading();
            return;
          }
          // Render into the (still-hidden) real grid first, THEN reveal + drop the
          // skeleton — the cards exist underneath the ghosts before they're removed,
          // so there's no blank gap / layout shift.
          roots.forEach(function (root) { try { renderInto(root, items, map); } catch (e) {} });
          applyStatus('populated');
          done();
          removeLoading();
        });
      } else {
        applyStatus(status);
        if (status === 'empty' || status === 'club') clearGrids();
        done(); // register even when empty/guest so hero chips + toolbar see 0 counts
        removeLoading();
      }
    }).catch(function (e) { removeLoading(); });
  }, function () { removeLoading(); });
})();
